import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

// FROZEN PAGE - DO NOT MODIFY WITHOUT USER REQUEST
const PrivacyPolicyPage = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | SmartIT Aylesbury"
        description="Privacy Policy for SmartIT - Learn how we collect, use, and protect your personal information. Laptop and PC repair services in Aylesbury."
        url="https://thesmartit.co.uk/privacy-policy"
        image="/og-image.png"
      />
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        
        {/* Header */}
        <section className="pt-28 pb-12 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 to-transparent" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                Privacy Policy
              </h1>
            </div>
            <p className="text-slate-400">SmartIT - Aylesbury, UK</p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-10">
              
              {/* Section 1 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
                <p className="text-slate-300 leading-relaxed">
                  SmartIT ("we", "our", "us") provides laptop and PC repair services in Aylesbury. 
                  This Privacy Policy explains how we collect, use, and protect your personal information. 
                  SmartIT is operated solely by one technician.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
                <p className="text-slate-300 leading-relaxed mb-3">
                  We may collect the following information when you contact us or submit a form:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                  <li>Name</li>
                  <li>Phone number</li>
                  <li>Email address</li>
                  <li>Details about your laptop or PC issue</li>
                  <li>Any additional information you choose to provide</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
                <p className="text-slate-300 leading-relaxed mb-3">
                  We use your information to:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                  <li>Contact you regarding your repair request</li>
                  <li>Provide quotes and service details</li>
                  <li>Schedule appointments</li>
                  <li>Improve our services</li>
                  <li>Maintain internal records</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">4. Sharing Your Information</h2>
                <p className="text-slate-300 leading-relaxed">
                  We do not sell or share your personal information with third parties, except when 
                  required by law or when necessary to provide our services.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">5. Data Storage & Security</h2>
                <p className="text-slate-300 leading-relaxed">
                  We store your information securely and take reasonable steps to protect it from 
                  unauthorized access, loss, or misuse.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
                <p className="text-slate-300 leading-relaxed mb-3">
                  You may request:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                  <li>Access to your personal data</li>
                  <li>Correction of inaccurate data</li>
                  <li>Deletion of your data</li>
                  <li>Withdrawal of consent</li>
                </ul>
                <p className="text-slate-300 leading-relaxed mt-4">
                  To make a request, contact us at:{" "}
                  <a 
                    href="mailto:smartit_itsolutions@outlook.com" 
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    smartit_itsolutions@outlook.com
                  </a>
                </p>
              </div>

              {/* Section 7 */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">7. Changes to This Policy</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may update this Privacy Policy from time to time. The latest version will 
                  always be available on this page.
                </p>
              </div>

              {/* Section 8 */}
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-white mb-4">8. Contact Information</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                  <p className="text-white font-semibold mb-2">SmartIT – Aylesbury, UK</p>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-blue-400" />
                    <a 
                      href="mailto:smartit_itsolutions@outlook.com" 
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      smartit_itsolutions@outlook.com
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

// FROZEN PAGE - DO NOT MODIFY WITHOUT USER REQUEST
export default PrivacyPolicyPage;
